"use client";

import type { ReactNode } from "react";
import { Image } from "@/components/ui/image";
import Link from "next/link";
import { AlertTriangle, ImagePlus, RotateCcw, TrendingUp } from "@/components/icons";
import { confidenceTone, severityTone } from "@/lib/labels/analysis-tone";
import { formatCents } from "@/lib/formatting/currency";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ConversionCTA } from "./conversion-cta";
import { GradingFeedbackPanel } from "./grading-feedback-panel";
import { AnalysisFields } from "./analysis-fields";
import type {
  GradingAnalysis,
  GradingBuyerPrice,
  GradingFeedbackAccess,
} from "./grading-session";

interface GradingResultsProps {
  results: GradingAnalysis;
  buyerPrice: GradingBuyerPrice | null;
  feedbackAccess: GradingFeedbackAccess;
  sessionId: string;
  onReset: () => void;
  imagePreviews: Array<string>;
}

export function GradingResults({
  results,
  buyerPrice,
  feedbackAccess,
  sessionId,
  onReset,
  imagePreviews,
}: GradingResultsProps) {
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-start">
      <div className="space-y-4 lg:sticky lg:top-20">
        <UploadedPhotos imagePreviews={imagePreviews} />
        <Button variant="outline" onClick={onReset} className="w-full">
          <RotateCcw className="h-4 w-4" />
          Grade another
        </Button>
        <GradingFeedbackPanel feedbackAccess={feedbackAccess} />
      </div>

      <div className="space-y-4">
        <Card>
          <CardContent className="space-y-0 p-5">
            <div className="flex items-center justify-between pb-3">
              <h2 className="text-xl font-semibold">Analysis results</h2>
              <Badge variant={confidenceTone(results.confidence)}>
                {results.confidence} confidence
              </Badge>
            </div>
            <AnalysisFields results={results} feedbackAccess={feedbackAccess} />
            <ConditionAssessment results={results} />
            {buyerPrice && (
              <BuyerPrice
                buyerPrice={buyerPrice}
                productTypeSlug={results.detectedProductType}
              />
            )}
            <QualityNotes issues={results.qualityIssues} />
            <Recommendations recommendations={results.recommendations} />
          </CardContent>
        </Card>
        <ConversionCTA
          productTypeName={results.productTypeName}
          sessionId={sessionId}
          detectedProductType={results.detectedProductType}
        />
      </div>
    </div>
  );
}

function UploadedPhotos({ imagePreviews }: { imagePreviews: Array<string> }) {
  const [first, ...rest] = imagePreviews;
  if (!first) {
    return (
      <div className="flex aspect-[4/3] items-center justify-center rounded-xl border bg-muted/30">
        <ImagePlus className="h-12 w-12 text-muted-foreground/40" />
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="relative aspect-[4/3] overflow-hidden rounded-xl border shadow-sm">
        <Image
          src={first}
          alt="Uploaded product"
          fill
          className="object-cover"
          unoptimized
        />
      </div>
      {rest.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {rest.map((src, index) => (
            <div
              key={src}
              className="relative aspect-square overflow-hidden rounded-lg border"
            >
              <Image
                src={src}
                alt={`Upload ${index + 2}`}
                fill
                className="object-cover"
                unoptimized
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultSection({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <Separator className="my-4" />
      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground">{title}</h3>
        {children}
      </div>
    </>
  );
}

function ConditionAssessment({ results }: { results: GradingAnalysis }) {
  if (!results.conditionJustification && !results.aiNarrative) return null;
  return (
    <ResultSection title="Condition assessment">
      {results.conditionJustification && (
        <p className="text-sm italic text-muted-foreground">
          {results.conditionJustification}
        </p>
      )}
      <p className="text-sm">{results.aiNarrative}</p>
    </ResultSection>
  );
}

function buyerPriceSource(buyerPrice: GradingBuyerPrice): string {
  if (buyerPrice.scope === "national") return "What buyers pay on Repackify";
  return `What buyers pay in ${buyerPrice.stateName}`;
}

function BuyerPrice({
  buyerPrice,
  productTypeSlug,
}: {
  buyerPrice: GradingBuyerPrice;
  productTypeSlug: string;
}) {
  return (
    <ResultSection title="What buyers pay">
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="text-xl font-bold">
          {formatCents(buyerPrice.avgCents)}
        </span>
        <span className="text-sm text-muted-foreground">
          avg per unit
        </span>
        <span className="text-sm text-muted-foreground">
          {formatCents(buyerPrice.minCents)} –{" "}
          {formatCents(buyerPrice.maxCents)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {buyerPriceSource(buyerPrice)}
      </p>
      <Link
        href={`/price-index/${productTypeSlug}`}
        className="inline-flex items-center gap-1 text-sm text-link hover:text-link-hover hover:underline"
      >
        <TrendingUp className="h-3.5 w-3.5" />
        View market data
      </Link>
    </ResultSection>
  );
}

function QualityNotes({
  issues,
}: {
  issues: GradingAnalysis["qualityIssues"];
}) {
  if (issues.length === 0) return null;
  const title = (
    <span className="inline-flex items-center gap-1.5">
      <AlertTriangle className="h-3.5 w-3.5 text-warning-strong" />
      Quality notes
    </span>
  );
  return (
    <ResultSection title={title}>
      <ul className="space-y-1.5">
        {issues.map((issue) => (
          <li key={issue.issue} className="text-sm">
            <Badge variant={severityTone(issue.severity)}>
              {issue.severity}
            </Badge>{" "}
            {issue.issue}
            <span className="text-muted-foreground">
              {" "}
              — {issue.impactOnValue}
            </span>
          </li>
        ))}
      </ul>
    </ResultSection>
  );
}

function Recommendations({
  recommendations,
}: {
  recommendations: Array<string>;
}) {
  if (recommendations.length === 0) return null;
  return (
    <ResultSection title="Recommendations">
      <ul className="list-disc space-y-1.5 pl-5 text-sm">
        {recommendations.map((recommendation) => (
          <li key={recommendation}>{recommendation}</li>
        ))}
      </ul>
    </ResultSection>
  );
}
